<template>
  <div class="interview-container">
  
    <!-- 未登录提示 -->
    <div v-if="!isLoggedIn" class="empty-state">
      <div class="empty-icon">🔒</div>
      <p>请先登录</p>
      <p class="empty-tip">登录后才能查看您的面试记录</p>
      <el-button type="primary" @click="goToLogin" class="login-btn">立即登录</el-button>
    </div>
    
    <!-- 记录列表 -->
    <div v-else-if="!showDetail" class="interview-content">
      <div v-if="records.length === 0" class="empty-state">
        <div class="empty-icon">📝</div>
        <p>暂无面试记录</p>
        <p class="empty-tip">完成AI面试后可以看到您的面试记录</p>
      </div>
      
      <div 
        v-for="record in records" 
        :key="record.id" 
        class="interview-card"
        @click="viewDetail(record)"
      >
        <div class="card-header">
          <div class="card-icon">🎯</div>
          <div class="card-info">
            <h3>面试记录</h3>
            <p class="card-time">{{ formatDate(record.createTime) }}</p>
          </div>
        </div>
        <div class="card-body">
          <div class="score-info">
            <span class="score-label">得分：</span>
            <span class="score-value">{{ record.score }}分</span>
            
          </div>
          <p class="evaluation-text">{{ record.evaluation }}</p>
        </div>
        <div class="card-footer">
          <span class="view-detail">查看详情 <i class="el-icon-arrow-right"></i></span>
        </div>
      </div>
      
      <!-- 分页组件 -->
      <div v-if="total > 0" class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :page-size="pageSize"
          :current-page="pageNum"
          :page-sizes="[5, 10, 20, 50]"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
    
    <!-- 记录详情 -->
    <div v-else class="detail-content">
      <div class="detail-header">
        <el-button icon="el-icon-back" @click="goBack" class="back-btn">返回列表</el-button>
        <h3>面试记录详情</h3>
      </div>
      
      <div class="detail-card">
        <div class="detail-score">
          <div class="score-circle">
            <span class="score-number">{{ currentRecord.score }}</span>
            <span class="score-unit">分</span>
          </div>
          <div class="score-meta">
            <div class="grade-badge" :class="currentRecord.grade?.toLowerCase()">
              {{ currentRecord.grade }}
            </div>
          </div>
        </div>
        
        <div class="detail-section">
          <h4>总体评价</h4>
          <p>{{ currentRecord.evaluation }}</p>
        </div>
        
        <div class="detail-section">
          <h4>改进建议</h4>
          <ul class="suggestions-list">
            <li v-for="(suggestion, index) in parsedSuggestions" :key="index">
              {{ suggestion }}
            </li>
          </ul>
        </div>
        
        <!-- 题目详情列表 -->
        <div v-if="currentRecord.questions && currentRecord.questions.length > 0" class="detail-section">
          <h4>答题详情</h4>
          <div class="questions-list">
            <div 
              v-for="(question, index) in currentRecord.questions" 
              :key="question.id || index" 
              class="question-item"
            >
              <div class="question-header">
                <span class="question-number">第 {{ index + 1 }} 题</span>
              </div>
              <div class="question-content">
                <span class="label">题目：</span>
                <span>{{ question.questionTitle }}</span>
              </div>
              <div class="answer-section">
                <div class="answer-item user-answer">
                  <span class="answer-label">你的回答：</span>
                  <p>{{ question.userAnswer }}</p>
                </div>
                <div class="answer-item suggested-answer">
                  <span class="answer-label">参考答案：</span>
                  <p>{{ question.suggestedAnswer }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="detail-info">
          <span>面试时间：{{ formatDate(currentRecord.createTime) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getInterviewRecords, getInterviewRecordDetail } from "@/api/processApi";

export default {
  name: "AIInterviewRecord",
  components: {},
  data() {
    return {
      isLoggedIn: false,
      records: [],
      showDetail: false,
      currentRecord: {},
      isLoading: false,
      // 分页参数
      pageNum: 1,
      pageSize: 2,
      total: 0,
      pages: 1
    };
  },
  computed: {
    parsedSuggestions() {
      if (!this.currentRecord.suggestions) return [];
      try {
        return JSON.parse(this.currentRecord.suggestions);
      } catch (e) {
        return [];
      }
    }
  },
  mounted() {
    this.checkLoginStatus();
  },
  methods: {
    // 检查登录状态
    checkLoginStatus() {
      const authData = localStorage.getItem("auth");
      if (authData) {
        try {
          const storedAuth = JSON.parse(authData);
          const authState = storedAuth.state || storedAuth;
          const token = authState._token;
          this.isLoggedIn = !!token;
        } catch (e) {
          console.error("解析登录状态失败", e);
          this.isLoggedIn = false;
        }
      }
      // 如果已登录，获取面试记录
      if (this.isLoggedIn) {
        this.fetchRecords();
      }
    },
    
    // 跳转到登录页
    goToLogin() {
      window.location.href = '/#/login';
    },
    
    fetchRecords(pageNum = this.pageNum, pageSize = this.pageSize) {
      this.isLoading = true;
      this.pageNum = pageNum;
      this.pageSize = pageSize;
      getInterviewRecords({ pageNum, pageSize }).then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          this.records = data.list || [];
          this.total = data.total || 0;
          this.pages = data.pages || 1;
        } else {
          this.records = [];
          this.total = 0;
          this.pages = 1;
        }
      }).catch(error => {
        console.error('获取面试记录失败:', error);
        this.records = [];
        this.total = 0;
        this.pages = 1;
      }).finally(() => {
        this.isLoading = false;
      });
    },
    viewDetail(record) {
      this.isLoading = true;
      getInterviewRecordDetail(record.id).then(response => {
        if (response.data.code === 200) {
          this.currentRecord = response.data.data || {};
          this.showDetail = true;
        } else {
          this.$message.error('获取记录详情失败');
        }
      }).catch(error => {
        console.error('获取记录详情失败:', error);
        this.$message.error('获取记录详情失败');
      }).finally(() => {
        this.isLoading = false;
      });
    },
    goBack() {
      this.showDetail = false;
      this.currentRecord = {};
      // 保持当前分页状态
      this.fetchRecords(this.pageNum, this.pageSize);
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchRecords(1, val);
    },
    handleCurrentChange(val) {
      this.fetchRecords(val, this.pageSize);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        return dateStr;
      }
    }
  },
};
</script>

<style scoped>
.interview-container {
  padding: 0 16px 12px;
  max-width: 100%;
  box-sizing: border-box;
}

.interview-container h2 {
  font-size: 20px;
  color: #333;
  margin-bottom: 12px;
}

.interview-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 8px 0;
  background: transparent;
  border-radius: 0;
}·······

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 12px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.empty-state p {
  font-size: 16px;
  color: #333;
  margin: 0 0 10px 0;
}

.empty-tip {
  font-size: 14px;
  color: #999;
}

.login-btn {
  margin-top: 20px;
  padding: 10px 32px;
  font-size: 15px;
}

.interview-card {
  background: #fff;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.3s;
  overflow-x: hidden;
  max-width: 100%;
  box-sizing: border-box;
}

.interview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.card-icon {
  font-size: 26px;
}

.card-info h3 {
  font-size: 16px;
  color: #333;
  margin: 0 0 4px 0;
}

.card-time {
  font-size: 13px;
  color: #999;
  margin: 0;
}

.card-body {
  padding: 6px 8px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 6px;
}

.score-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.score-label {
  font-size: 14px;
  color: #666;
}

.score-value {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.evaluation-text {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  text-align: right;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.view-detail {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #409eff;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.view-detail:hover {
  background: #ecf5ff;
  color: #66b1ff;
  transform: translateX(4px);
}

.view-detail i {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.view-detail:hover i {
  transform: translateX(4px);
}

/* 详情页面样式 */
.detail-content {
  background: #fff;
  border-radius: 8px;
  padding: 8px 16px 16px;
  overflow: hidden;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: 0.5px;
}

.back-btn {
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  color: #666666;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: #f5f7fa;
  border-color: #409eff;
  color: #409eff;
  transform: translateX(-2px);
}

.detail-card {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  overflow-x: hidden;
  overflow-y: visible;
}

.detail-score {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e0e0e0;
}

.score-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.grade-badge {
  font-size: 20px;
  font-weight: 600;
  padding: 8px 24px;
  border-radius: 20px;
}

.grade-badge.优秀 {
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
}

.grade-badge.良好 {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
}

.grade-badge.合格 {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.grade-badge.不合格 {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.score-circle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 6px 14px rgba(102, 126, 234, 0.3);
  position: relative;
  animation: scorePulse 2s ease-in-out infinite;
}

.score-circle::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0.25;
  filter: blur(6px);
}

.score-number {
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
  position: relative;
  z-index: 1;
}

.score-unit {
  font-size: 14px;
  margin-top: 4px;
  opacity: 0.9;
  position: relative;
  z-index: 1;
}

@keyframes scorePulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 30px rgba(102, 126, 234, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.5);
  }
}

.detail-section {
  margin-bottom: 12px;
}

.detail-section h4 {
  font-size: 15px;
  color: #333;
  margin: 0 0 8px 0;
}

.detail-section p {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

.suggestions-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestions-list li {
  position: relative;
  padding: 6px 8px 6px 24px;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 6px;
  background: #fff;
  border-radius: 4px;
  border-left: 3px solid #667eea;
}

.suggestions-list li::before {
  content: "•";
  position: absolute;
  left: 12px;
  color: #667eea;
  font-weight: bold;
}

/* 题目列表样式 */
.questions-list {
  margin-top: 10px;
}

.question-item {
  background: #fff;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
  border: 1px solid #e8e8e8;
}

.question-item:last-child {
  margin-bottom: 0;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e0e0e0;
}

.question-number {
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
}

.question-content {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 12px;
  color: #333;
}

.question-content .label {
  color: #666;
  font-weight: 500;
}

.answer-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.answer-item {
  padding: 10px;
  border-radius: 6px;
}

.answer-item.user-answer {
  background: #f0f9ff;
  border-left: 3px solid #0ea5e9;
}

.answer-item.suggested-answer {
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
}

.answer-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
}

.user-answer .answer-label {
  color: #0ea5e9;
}

.suggested-answer .answer-label {
  color: #f59e0b;
}

.answer-item p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #444;
}

.detail-info {
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
  font-size: 13px;
  color: #999;
  text-align: center;
}

@media (max-width: 768px) {
  .interview-card {
    padding: 16px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .detail-score {
    flex-direction: column;
  }
}
</style>